import { Button, Icon, Slider, Typography, Spinner } from '@scale/scaleui';
import clsx from 'clsx';
import { memo, useEffect, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { v4 } from 'uuid';
import WavEncoder from 'wav-encoder';
import { clipRegionsOutOfAudioBuffer, getColorFromNode } from './wavesurfer-utils';
import logger from 'react-scale/lib/logging';

/**
 * WARNING: This component MUST be used only on the client-side.
 * WaveSurfer.js relies heavily on browser APIs and will break if rendered on the server-side.
 * Make sure to use dynamic imports or similar techniques to ensure server-side safety.
 */

const MIN_ZOOM = 0;
const MAX_ZOOM = 200;

type Props = {
  originalBlob?: Blob;
  src?: string;
  onFinish?: (blob: Blob) => void;
  readonly?: boolean;
  className?: string;
  width?: number;
};

const safeDivide = (a: number, b: number) => (b > 0 ? a / b : 0);

export const ChatAudioEditor = memo(
  ({ originalBlob, src, onFinish, readonly, className, width }: Props) => {
    const [idSuffix] = useState(() => v4());
    const waveSurferContainerId = `waveform-${idSuffix}`;
    const timelineContainerId = `timeline-${idSuffix}`;
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const [isLoading, setIsLoading] = useState(!originalBlob && !!src);

    useEffect(() => {
      if (originalBlob) {
        setAudioBlob(originalBlob);
        setIsLoading(false);
        return;
      }

      if (!src) {
        return;
      }

      const loadAudioFromUrl = async () => {
        try {
          setIsLoading(true);
          const response = await fetch(src);
          const blob = await response.blob();
          setAudioBlob(blob);
        } catch (error) {
          logger.error('Error loading audio:', error);
        } finally {
          setIsLoading(false);
        }
      };

      loadAudioFromUrl();
    }, [originalBlob, src]);

    const waveformRef = useRef(null);
    const timelineRef = useRef(null);
    const wavesurferRef = useRef<WaveSurfer | null>(null);

    const [hasRegions, setHasRegions] = useState(false);
    const [regionColor, setRegionColor] = useState<string | undefined>();
    const [waveColor, setWaveColor] = useState<string | undefined>();
    const [progressColor, setProgressColor] = useState<string | undefined>();
    const [audioUrl, setAudioUrl] = useState<string>('');

    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef<HTMLAudioElement>(null);

    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    const handleTimeUpdate = () => {
      if (audioRef.current && isWaveformLoading) {
        setCurrentTime(audioRef.current.currentTime);
      }
    };

    const handleLoadedMetadata = () => {
      if (audioRef.current) {
        setDuration(audioRef.current.duration);
      }
    };

    const handleSeek = (value: number) => {
      if (audioRef.current && isWaveformLoading) {
        audioRef.current.currentTime = value;
        setCurrentTime(value);
      }
    };

    const handleClickPlayPause = () => {
      if (isWaveformLoading && audioRef.current) {
        if (audioRef.current.paused) {
          audioRef.current.play();
        } else {
          audioRef.current.pause();
        }
        return;
      }

      const waveSurfer = wavesurferRef.current;
      if (waveSurfer === null) {
        return;
      }
      if (waveSurfer.isPlaying()) {
        waveSurfer.pause();
      } else {
        waveSurfer.play();
      }
    };

    const handleSkipBackward = () => {
      if (isWaveformLoading && audioRef.current) {
        const newTime = Math.max(0, audioRef.current.currentTime - 5);
        audioRef.current.currentTime = newTime;
        return;
      }

      const waveSurfer = wavesurferRef.current;
      if (waveSurfer === null) {
        return;
      }
      const currentTime = waveSurfer.getCurrentTime();
      const newTime = Math.max(0, currentTime - 5);
      waveSurfer.seekTo(newTime / waveSurfer.getDuration());
    };

    const handleSkipForward = () => {
      if (isWaveformLoading && audioRef.current) {
        const newTime = Math.min(audioRef.current.duration, audioRef.current.currentTime + 5);
        audioRef.current.currentTime = newTime;
        return;
      }

      const waveSurfer = wavesurferRef.current;
      if (waveSurfer === null) {
        return;
      }
      const currentTime = waveSurfer.getCurrentTime();
      const duration = waveSurfer.getDuration();
      const newTime = Math.min(duration, currentTime + 5);
      waveSurfer.seekTo(safeDivide(newTime, duration));
    };

    const [playbackSpeed, setPlaybackSpeed] = useState(1);

    // Add new loading state
    const [isWaveformLoading, setIsWaveformLoading] = useState(true);

    const formWaveSurferOptions = (ref: any) => ({
      container: ref,
      waveColor: waveColor || '#A8DBA8',
      progressColor: progressColor || '#3B8686',
      backend: 'MediaElement',
      mediaControls: false,
      hideScrollbar: false,
      normalize: true,
      autoCenter: true,
      height: 128,
      cursorWidth: 1,
      fillParent: true,
      interact: true,
      minPxPerSec: MIN_ZOOM,
    });

    const create = async () => {
      if (!waveformRef.current) {
        return;
      }
      const WaveSurfer = (await import('wavesurfer.js')).default;
      const RegionsPlugin = (await import('wavesurfer.js/src/plugin/regions')).default;
      const TimelinePlugin = (await import('wavesurfer.js/src/plugin/timeline')).default;

      setWaveColor(
        getColorFromNode({
          node: waveformRef.current,
          colorVarName: '--color-info-600',
          opacity: 1,
        }),
      );
      setProgressColor(
        getColorFromNode({
          node: waveformRef.current,
          colorVarName: '--color-info-400',
          opacity: 1,
        }),
      );
      setRegionColor(
        getColorFromNode({
          node: waveformRef.current,
          colorVarName: '--color-danger-400',
          opacity: 0.1,
        }),
      );

      const options = formWaveSurferOptions(waveformRef.current);
      // @ts-ignore
      options.plugins = [
        RegionsPlugin.create({
          dragSelection: !readonly,
          color: regionColor,
        }),
        TimelinePlugin.create({
          container: `#${timelineContainerId}`,
        }),
      ];
      // @ts-ignore
      wavesurferRef.current = WaveSurfer.create(options);

      // reset state
      setIsPlaying(false);
      setHasRegions(false);
      setIsWaveformLoading(true);

      // Load the initial audio
      // @ts-ignore
      const initAudioUrl = URL.createObjectURL(audioBlob);
      wavesurferRef.current.load(initAudioUrl);
      setAudioUrl(initAudioUrl);

      // wavesurfer event listeners
      wavesurferRef.current.on('ready', () => {
        if (audioRef.current) {
          const currentTime = audioRef.current.currentTime;
          const duration = audioRef.current.duration;
          const wasPlaying = !audioRef.current.paused;
          const currentSpeed = audioRef.current.playbackRate;

          wavesurferRef.current?.setPlaybackRate(currentSpeed);
          if (duration > 0) {
            wavesurferRef.current?.seekTo(safeDivide(currentTime, duration));
          } else {
            logger.warn('Audio duration is zero or undefined. Skipping seekTo operation.');
          }
          if (wasPlaying) {
            wavesurferRef.current?.play();
          }
        }
        setIsWaveformLoading(false);
      });

      wavesurferRef.current.on('finish', () => {
        setIsPlaying(false);
      });
      wavesurferRef.current.on('play', () => {
        setIsPlaying(true);
      });
      wavesurferRef.current.on('pause', () => {
        setIsPlaying(false);
      });
      // region event listeners
      wavesurferRef.current.on('region-created', () => {
        setHasRegions(true);
      });
    };

    // controls wavesurfer and plugin refs
    useEffect(() => {
      if (!audioBlob || isLoading) return;

      create();

      return () => {
        if (wavesurferRef.current) {
          // Clean up the URL object
          URL.revokeObjectURL(audioUrl);

          // destroy wavesurfer and plugins
          wavesurferRef.current.destroy();
          wavesurferRef.current.unAll();
          wavesurferRef.current = null;
        }
      };
    }, [
      audioBlob,
      isLoading,
      waveSurferContainerId,
      timelineContainerId,
      readonly,
      regionColor,
      waveColor,
      progressColor,
    ]);

    // Handle clipping selected regions
    const { mutate: handleClip, isLoading: clipLoading } = useMutation(async () => {
      if (readonly) {
        return;
      }

      const wavesurfer = wavesurferRef.current;
      if (!wavesurfer) return;

      // We need to switch to WebAudio backend temporarily for processing
      const arrayBuffer = await audioBlob!.arrayBuffer();
      const audioContext = new AudioContext();
      const originalBuffer = await audioContext.decodeAudioData(arrayBuffer);

      const newBuffer = await clipRegionsOutOfAudioBuffer({
        audioContext,
        originalBuffer,
        regions: Object.values(wavesurferRef.current?.regions.list ?? {}),
      });

      if (newBuffer === null) {
        return;
      }

      const newChannelData: Float32Array[] = [];
      for (let channelIndex = 0; channelIndex < newBuffer.numberOfChannels; channelIndex++) {
        newChannelData.push(newBuffer.getChannelData(channelIndex));
      }

      const wavEncodedData = await WavEncoder.encode({
        sampleRate: newBuffer.sampleRate,
        channelData: newChannelData,
      });

      onFinish?.(new Blob([wavEncodedData], { type: 'audio/wav' }));
    });

    // drag to control zoom
    const [zoomValue, setZoomValue] = useState(MIN_ZOOM);
    const handleZoomChange = (updatedZoomValue: number) => {
      const wavesurfer = wavesurferRef.current;
      if (wavesurfer === null || zoomValue === updatedZoomValue) return;

      wavesurfer.zoom(updatedZoomValue);
      setZoomValue(updatedZoomValue);
    };

    const handleNativePlay = () => {
      if (isWaveformLoading) {
        setIsPlaying(true);
      }
    };

    const handleNativePause = () => {
      if (isWaveformLoading) {
        setIsPlaying(false);
      }
    };

    const handleNativeEnded = () => {
      if (isWaveformLoading) {
        setIsPlaying(false);
      }
    };

    const handleSpeedChange = (speed: number) => {
      if (isWaveformLoading && audioRef.current) {
        audioRef.current.playbackRate = speed;
        setPlaybackSpeed(speed);
        return;
      }

      const wavesurfer = wavesurferRef.current;
      if (wavesurfer === null) return;

      const currentTime = wavesurfer.getCurrentTime();
      const duration = wavesurfer.getDuration();
      const isPlaying = wavesurfer.isPlaying();

      wavesurfer.setPlaybackRate(speed);
      setPlaybackSpeed(speed);

      // Calculate position as a ratio between 0 and 1
      const position = safeDivide(currentTime, duration);
      wavesurfer.seekTo(position);

      if (isPlaying) {
        wavesurfer.play();
      }
    };

    if (isLoading) {
      return (
        <div className="w-full p-4 flex items-center justify-center gap-2 border-dashed border border-gray-200 rounded-md">
          <Spinner size="sm" />
          <Typography.Caption>Loading audio player</Typography.Caption>
        </div>
      );
    }

    if (!audioBlob) {
      return null;
    }

    return (
      <div className={clsx('flex flex-col gap-2 p-2', className)} style={{ width }}>
        <div className="relative h-full w-full">
          {isWaveformLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-white z-10">
              <audio
                ref={audioRef}
                src={audioUrl}
                controls
                style={{ display: 'none' }}
                className="w-full max-w-md mb-4"
                onPlay={handleNativePlay}
                onPause={handleNativePause}
                onEnded={handleNativeEnded}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
              />
              <div className="flex flex-col items-center gap-2 text-center w-full">
                <div className="w-full flex flex-col gap-2">
                  <div
                    className="relative w-full h-32 flex items-center cursor-pointer bg-white"
                    onClick={e => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const percentage = x / rect.width;
                      const newTime = percentage * duration;
                      handleSeek(newTime);
                    }}
                  >
                    <div className="absolute w-full h-[1px] bg-gray-200" />
                    <div
                      className="absolute h-full w-[1px] bg-black"
                      style={{
                        left: `${safeDivide(currentTime, duration) * 100}%`,
                        transform: 'translateX(-50%)',
                      }}
                    />
                    <div
                      className="absolute w-full text-center text-gray-500 text-sm"
                      style={{ top: '25%' }}
                    >
                      Waveform Loading (You can still play the Audio)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div
            id={waveSurferContainerId}
            ref={waveformRef}
            style={{
              height: '100%',
            }}
            className="cursor-pointer"
          />
        </div>
        <div
          id={timelineContainerId}
          ref={timelineRef}
          style={{
            height: '50px',
            marginTop: '10px',
          }}
        />
        <footer
          className="grid gap-4 min-w-fit min-h-fit"
          style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
        >
          <Slider
            className="my-auto"
            step={1}
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            label="Zoom"
            value={zoomValue}
            setValue={handleZoomChange}
            iconLeft={['fas', 'magnifying-glass']}
          />

          <div className="flex justify-center items-center gap-1 min-w-fit min-h-fit">
            <Icon
              size="lg"
              variant="neutral"
              icon={['fas', 'backward']}
              tooltip="Replay"
              hasBackground
              onClick={() => {
                const wavesurfer = wavesurferRef.current;
                if (wavesurfer !== null) {
                  wavesurfer.seekTo(0);
                  wavesurfer.play();
                  setIsPlaying(true);
                }
              }}
            />

            <Icon
              size="lg"
              variant="neutral"
              icon={['fas', 'backward-step']}
              tooltip="Skip back 5 seconds"
              hasBackground
              onClick={handleSkipBackward}
            />

            <Icon
              size="lg"
              variant="neutral"
              icon={['fas', isPlaying ? 'pause' : 'play']}
              onClick={handleClickPlayPause}
              tooltip={isPlaying ? 'Pause' : 'Play'}
              hasBackground
            />

            <Icon
              size="lg"
              variant="neutral"
              icon={['fas', 'forward-step']}
              tooltip="Skip forward 5 seconds"
              hasBackground
              onClick={handleSkipForward}
            />

            {/* Playback speed control buttons */}
            <div className="flex gap-1">
              {[0.5, 0.75, 1, 1.5, 2].map(speed => (
                <Button
                  key={speed}
                  size="sm"
                  variant={playbackSpeed === speed ? 'primary' : 'secondary'}
                  onClick={() => handleSpeedChange(speed)}
                  className="min-w-[40px]"
                >
                  {speed}x
                </Button>
              ))}
            </div>
          </div>

          {!readonly && (
            <div className="flex justify-end items-center gap-1 min-w-fit min-h-fit">
              {hasRegions ? (
                <>
                  <Button
                    onClick={() => {
                      wavesurferRef.current?.regions.clear();
                      setHasRegions(false);
                    }}
                    variant="secondary"
                    className="min-w-fit min-h-fit"
                    disabled={clipLoading}
                  >
                    Clear selection
                  </Button>
                  <Button
                    onClick={() => handleClip()}
                    variant="danger"
                    className="min-w-fit min-h-fit"
                    disabled={clipLoading}
                  >
                    Delete selected parts
                  </Button>
                </>
              ) : (
                <Typography.Caption>Click and drag over the audio wave to edit</Typography.Caption>
              )}
            </div>
          )}
        </footer>
      </div>
    );
  },
);
